import { Dropdown, Icons, useToggle } from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  Filing,
  FilingReviewStatus,
  FilingWorkflowStatus,
} from '../../molecules';
import { ManageAssigneesModal } from '../assignees/ManageAssigneesModal';
import { ArchiveFilingModal } from './modals/ArchiveFilingModal';
import { CompleteFilingModal } from './modals/CompleteFilingModal';
import { PurchaseFilingModal } from './modals/PurchaseFilingModal';

interface FilingDropdownProps {
  filing: Filing;
  onComplete: (filingId: string, file: FileBasic) => Promise<boolean>;
  onArchive: (filingId: string, reason: string) => Promise<boolean>;
  onRefresh: () => Promise<void>;
  onDownloadReceipt: () => Promise<boolean>;
  showPurchaseOption?: boolean;
  showManageAssignees?: boolean;
  availableAssignees: Array<{ id: string; name: string; email: string }>;
  onAddAssignees: (assigneeIds: string[]) => Promise<boolean>;
  onRemoveAssignee: (assigneeId: string) => Promise<boolean>;
  onSuccessAssignees: () => void;
  loggedInUserEmail: string;
  companyId: number;
}

const FilingDropdown = ({
  filing,
  onComplete,
  onArchive,
  onRefresh,
  onDownloadReceipt,
  showPurchaseOption = false,
  showManageAssignees = true,
  availableAssignees,
  onAddAssignees,
  onRemoveAssignee,
  onSuccessAssignees,
  loggedInUserEmail,
  companyId,
}: FilingDropdownProps) => {
  const manageAssigneesModalVisibility = useToggle();
  const completeFilingModalVisibility = useToggle();
  const archiveFilingModalVisibility = useToggle();
  const purchaseFilingModalVisibility = useToggle();

  const isCompletedOrFiled = filing.filingStatus === FilingReviewStatus.FILED;
  const isArchived =
    filing.filingWorkflowStatus === FilingWorkflowStatus.ARCHIVED;

  const dropdownItems = [
    // Download receipt for completed filings
    isCompletedOrFiled && {
      label: 'Download receipt',
      icon: Icons.Download01,
      onClick: onDownloadReceipt,
    },
    // Upload receipt for incomplete filings
    !isCompletedOrFiled && {
      label: 'Upload receipt',
      icon: Icons.Upload01,
      onClick: completeFilingModalVisibility.show,
    },
    // Manage/View assignees
    showManageAssignees && {
      label: isCompletedOrFiled ? 'View assignees' : 'Manage assignees',
      icon: Icons.Users01,
      onClick: manageAssigneesModalVisibility.show,
    },
    // Archive option for active filings
    !isCompletedOrFiled &&
      !isArchived && {
        label: 'Archive filing',
        icon: Icons.Archive,
        onClick: archiveFilingModalVisibility.show,
        labelClassName: 'text-utility-error-600',
        iconClassName: 'text-utility-error-600',
      },
  ].filter(Boolean);

  return (
    <>
      <Dropdown
        menu={{
          groups: [{ items: dropdownItems }],
        }}
        align="end"
      >
        <div className="size-8 flex justify-center items-center group">
          <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
        </div>
      </Dropdown>

      {showManageAssignees && (
        <ManageAssigneesModal
          isOpen={manageAssigneesModalVisibility.visible}
          onClose={manageAssigneesModalVisibility.toggle}
          filing={filing}
          availableAssignees={availableAssignees}
          onAddAssignees={onAddAssignees}
          onRemoveAssignee={onRemoveAssignee}
          onSuccess={onSuccessAssignees}
          loggedInUserEmail={loggedInUserEmail}
        />
      )}

      <CompleteFilingModal
        isOpen={completeFilingModalVisibility.visible}
        onClose={completeFilingModalVisibility.toggle}
        filing={filing}
        onComplete={onComplete}
        onSuccess={onRefresh}
        companyId={companyId}
      />

      <ArchiveFilingModal
        isOpen={archiveFilingModalVisibility.visible}
        onClose={archiveFilingModalVisibility.toggle}
        filing={filing}
        onArchive={onArchive}
        onSuccess={onRefresh}
      />

      {showPurchaseOption && (
        <PurchaseFilingModal
          isOpen={purchaseFilingModalVisibility.visible}
          onClose={purchaseFilingModalVisibility.toggle}
          filing={filing}
        />
      )}
    </>
  );
};

export { FilingDropdown };
